import React, { useState, useEffect } from 'react'

const CST_PIS_COFINS = [
  { cod: '01', desc: 'Operação tributável — alíquota básica' },
  { cod: '02', desc: 'Operação tributável — alíquota diferenciada' },
  { cod: '03', desc: 'Operação tributável — alíquota por unidade de medida' },
  { cod: '04', desc: 'Operação tributável monofásica — revenda a alíquota zero' },
  { cod: '05', desc: 'Operação tributável por substituição tributária' },
  { cod: '06', desc: 'Operação tributável a alíquota zero' },
  { cod: '07', desc: 'Operação isenta da contribuição' },
  { cod: '08', desc: 'Operação sem incidência da contribuição' },
  { cod: '09', desc: 'Operação com suspensão da contribuição' },
  { cod: '49', desc: 'Outras operações de saída' },
]
import api from '../services/api'

interface Servico {
  id:                      number
  descricao:               string
  cod_tributacao_nacional:  string
  cod_tributacao_municipal: string
  nbs:                     string
  aliquota_iss:            string
  piscofins_cst:           string
  perc_inss:               string
  ativo:                   boolean
}

const vazio: Omit<Servico, 'id' | 'ativo'> = {
  descricao:               '',
  cod_tributacao_nacional:  '',
  cod_tributacao_municipal: '',
  nbs:                     '',
  aliquota_iss:            '5.00',
  piscofins_cst:           '07',
  perc_inss:               '0.00',
}

export default function CadastroServicosNFSe() {
  const [lista, setLista]       = useState<Servico[]>([])
  const [form, setForm]         = useState({ ...vazio })
  const [editId, setEditId]     = useState<number | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro]         = useState('')

  async function carregar() {
    const r = await api.get('/nfse-servicos/todos')
    setLista(r.data)
  }

  useEffect(() => { carregar() }, [])

  function novoForm() {
    setForm({ ...vazio })
    setEditId(null)
    setErro('')
  }

  function editarItem(s: Servico) {
    setForm({
      descricao:               s.descricao,
      cod_tributacao_nacional:  s.cod_tributacao_nacional,
      cod_tributacao_municipal: s.cod_tributacao_municipal,
      nbs:                     s.nbs,
      aliquota_iss:            s.aliquota_iss,
      piscofins_cst:           s.piscofins_cst,
      perc_inss:               s.perc_inss,
    })
    setEditId(s.id)
    setErro('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function salvar() {
    if (!form.descricao.trim() || !form.cod_tributacao_nacional.trim()) {
      setErro('Descrição e Cód. de Tributação Nacional são obrigatórios.')
      return
    }
    setSalvando(true)
    setErro('')
    try {
      if (editId !== null) {
        await api.put(`/nfse-servicos/${editId}`, form)
      } else {
        await api.post('/nfse-servicos', form)
      }
      novoForm()
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  async function excluir(id: number, descricao: string) {
    if (!window.confirm(`Excluir "${descricao}"?`)) return
    await api.delete(`/nfse-servicos/${id}`)
    await carregar()
    if (editId === id) novoForm()
  }

  async function toggleAtivo(s: Servico) {
    await api.put(`/nfse-servicos/${s.id}`, { ...s, ativo: !s.ativo })
    await carregar()
  }

  return (
    <div style={{ maxWidth: 760 }}>
      {/* Formulário */}
      <div className="card" style={{ marginBottom: '1.2rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>
          {editId !== null ? 'Editar Serviço' : 'Novo Serviço'}
        </h3>

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Descrição *</label>
            <input
              type="text"
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              placeholder="Ex: Serviços de transporte de passageiros"
              maxLength={200}
            />
          </div>

          <div className="field">
            <label>Cód. de Tributação Nacional *</label>
            <input
              type="text"
              value={form.cod_tributacao_nacional}
              onChange={e => setForm(f => ({ ...f, cod_tributacao_nacional: e.target.value.slice(0, 20) }))}
              placeholder="Ex: 010101"
            />
          </div>

          <div className="field">
            <label>NBS</label>
            <input
              type="text"
              value={form.nbs}
              onChange={e => setForm(f => ({ ...f, nbs: e.target.value.slice(0, 15) }))}
              placeholder="Ex: 1.0501.00.00"
            />
          </div>

          <div className="field">
            <label>CST PIS/COFINS padrão</label>
            <select
              value={form.piscofins_cst}
              onChange={e => setForm(f => ({ ...f, piscofins_cst: e.target.value }))}
            >
              {CST_PIS_COFINS.map(c => (
                <option key={c.cod} value={c.cod}>{c.cod} — {c.desc}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>% INSS padrão (retenção)</label>
            <input
              type="text"
              value={form.perc_inss}
              onChange={e => setForm(f => ({ ...f, perc_inss: e.target.value }))}
              placeholder="0.00"
            />
          </div>
        </div>

        {erro && (
          <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : editId !== null ? 'Salvar alterações' : 'Adicionar'}
          </button>
          {editId !== null && (
            <button className="btn btn-outline" onClick={novoForm}>Cancelar</button>
          )}
        </div>
      </div>

      {/* Lista */}
      <div className="card">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>
          Serviços cadastrados ({lista.filter(s => s.ativo).length} ativos)
        </h3>

        {lista.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum serviço cadastrado.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lista.map(s => (
            <div
              key={s.id}
              style={{
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
                opacity: s.ativo ? 1 : 0.5,
                background: editId === s.id ? '#eff6ff' : '#fff',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>{s.descricao}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>Trib. Nacional: <b>{s.cod_tributacao_nacional}</b></span>
                  {s.nbs && <span>NBS: <b>{s.nbs}</b></span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-outline btn-sm" onClick={() => editarItem(s)}>Editar</button>
                <button
                  className="btn btn-outline btn-sm"
                  style={{ color: s.ativo ? '#b45309' : '#15803d' }}
                  onClick={() => toggleAtivo(s)}
                  title={s.ativo ? 'Desativar' : 'Ativar'}
                >
                  {s.ativo ? 'Desativar' : 'Ativar'}
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  style={{ color: '#b91c1c' }}
                  onClick={() => excluir(s.id, s.descricao)}
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
